import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, Divider } from '@mui/material';
import MediaQuery from 'react-responsive';
import TableComponent from '../table/TableComponent';
import JncSectionTitle from '../global/JncSectionTitle';
import JncDialogTitle from '../modals/JncDialogTitle';
import { formatFileSize, useJunoCommonFunctions } from '../../../helpers';
import CustomPagination from '../pagination/CustomPagination';
import PropTypes from 'prop-types';
import DataTable from '../../../../../new-ui/components/DataTable';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import Pagination from '../../../../../new-ui/components/Pagination';
import ActionConfirmModal from '../../../../../new-ui/components/common/ActionConfirmModal';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import JunoButton from '../../../../../new-ui/components/common/JunoButton';

const MobileTabletDocumentsList = ({ onViewAll, files, imageBaseUrl, onRemoveFile }) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);

    const handleOpenDeleteModal = (index) => {
        setFileToDelete(files[index]);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setFileToDelete(null);
    };

    const handleConfirmDelete = () => {
        if (fileToDelete) {
            const index = files.findIndex(file => file.Name === fileToDelete.Name);
            if (index !== -1) {
                onRemoveFile(index);
            }
        }
        handleCloseDeleteModal();
    };

    return (
        <div className="rounded-lg outline outline-1 outline-offset-[-1px] outline-bulkpay-card-outline bg-bulkpay-card-bg mt-1 mb-1">
            <div className="flex items-center justify-between">
                <h2 className="text-text pt-4 px-4  uppercase  font-inter text-lg font-semibold leading-7">
                    Supporting Documents
                </h2>
            </div>
            <div className="p-4">
                {files.slice(0, 5).map((file, index) => (
                    <div key={index}>
                        {/* Document Row */}
                        <div className="flex items-center justify-between py-2.5">
                            <div className="flex items-center flex-1 min-w-0">
                                <DescriptionIcon
                                    alt="file"
                                    className="w-4 h-4 flex-shrink-0 text-text"
                                />
                                <div className="ml-2 min-w-0">
                                    <p className="text-text truncate text-sm font-medium leading-5 uppercase">
                                        {file.Name}
                                    </p>
                                    <p className="text-balanceCard-manage truncate text-xs leading-[18px]">
                                        {file.Size}
                                    </p>
                                </div>
                            </div>
                            <button
                                className="ml-2 flex-shrink-0"
                                onClick={() => handleOpenDeleteModal(index)}>
                                <DeleteIcon alt="delete" className="w-5 h-5 text-text" />
                            </button>
                        </div>

                        {/* Divider after document (but not after the last one) */}
                        {index < files.length - 1 && (
                            <div className="self-stretch h-px mt-3 bg-input-border" />
                        )}
                    </div>
                ))}

                <JunoButton
                    className="py-[12px] mt-3 font-semibold text-center rounded border-none bg-gray-200 text-gray-900 w-full lg:hidden"
                    type="secondary"
                    onClick={onViewAll}>
                    See all
                </JunoButton>
            </div>

            {showDeleteModal && fileToDelete && (
                <ActionConfirmModal
                    open={showDeleteModal}
                    heading="Delete Supporting Document!"
                    subHeading="Are you sure you want to delete the supporting document?"
                    onCancel={handleCloseDeleteModal}
                    onConfirm={handleConfirmDelete}
                    type="delete"
                    actionButtonText="delete"
                    body={() => (
                        <div>
                            <p className="!text-primary text-center text-base font-semibold leading-6 uppercase">
                                {fileToDelete.Name}
                            </p>
                            <p className="!text-gray-custom text-center text-sm font-medium leading-5 uppercase">
                                {fileToDelete.Size}
                            </p>
                        </div>
                    )}
                />
            )}
        </div>
    );
};

MobileTabletDocumentsList.propTypes = {
    files: PropTypes.array.isRequired,
    imageBaseUrl: PropTypes.string.isRequired,
    onRemoveFile: PropTypes.func.isRequired,
    onViewAll: PropTypes.func.isRequired,
};

const DocumentsModal = ({ files, open, onClose, imageBaseUrl, onRemoveFile, isMobile }) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);

    const handleOpenDeleteModal = (index) => {
        setFileToDelete(files[index]);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setFileToDelete(null);
    };

    const handleConfirmDelete = () => {
        if (fileToDelete) {
            const index = files.findIndex(file => file.Name === fileToDelete.Name);
            if (index !== -1) {
                onRemoveFile(index);
            }
        }
        handleCloseDeleteModal();
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullScreen={isMobile}
                fullWidth={!isMobile}
                className="!bg-bulkpay-card-bg !text-text "
                PaperProps={{
                    style: {
                        width: isMobile ? '100%' : '570px',
                    },
                }}>
                <div className="!bg-rootStyle-bg !h-full">
                    <div className=" !bg-bulkpay-card-bg flex items-center gap-10 !px-4 !py-3.5 !mb-2  rounded-b-lg">
                        <ArrowBackIcon onClick={onClose} className='!text-text' />
                        <p className="bg-bulkpay-card-bg !text-text font-inter text-lg font-semibold leading-7 uppercase">
                            Supporting Documents
                        </p>
                    </div>

                    <DialogContent className="bg-bulkpay-card-bg !px-4 !pb-4 !pt-2 rounded-t-lg">
                        <div>
                            {files.map((file, index) => (
                                <div key={index}>
                                    {/* Document Row */}
                                    <div className="flex items-center justify-between py-2.5">
                                        <div className="flex items-center flex-1 min-w-0">
                                            <DescriptionIcon
                                                alt="file"
                                                className="w-4 h-4 flex-shrink-0 text-text"
                                            />
                                            <div className="ml-2 min-w-0">
                                                <p className="text-balanceCard-manage text-sm font-medium leading-none uppercase mb-2">
                                                    {file.Name}
                                                </p>
                                                <p className="text-text uppercase text-sm leading-tight font-medium">
                                                    {file.Size}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            className="ml-2 flex-shrink-0"
                                            onClick={() => handleOpenDeleteModal(index)}>
                                            <DeleteIcon
                                                alt="delete"
                                                className="w-5 h-5 text-text"
                                            />
                                        </button>
                                    </div>

                                    {/* Divider after each document except last */}
                                    {index < files.length - 1 && (<div className="self-stretch h-px mt-3 bg-input-border" />)}
                                </div>
                            ))}
                        </div>
                    </DialogContent>
                </div>
            </Dialog>

            {showDeleteModal && fileToDelete && (
                <ActionConfirmModal
                    open={showDeleteModal}
                    heading="Delete Supporting Document!"
                    subHeading="Are you sure you want to delete the supporting document?"
                    onCancel={handleCloseDeleteModal}
                    onConfirm={handleConfirmDelete}
                    type="delete"
                    actionButtonText="delete"
                    body={() => (
                        <div>
                            <p className="text-[#18181B] dark:text-[#FFF] text-center text-base font-semibold leading-6 uppercase">
                                {fileToDelete.Name}
                            </p>
                            <p className="text-[#51525C] dark:text-[#D1D1D6] text-center text-sm font-medium leading-5 uppercase">
                                {fileToDelete.Size}
                            </p>
                        </div>
                    )}
                />
            )}
        </>
    );
};

DocumentsModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    files: PropTypes.array.isRequired,
    imageBaseUrl: PropTypes.string.isRequired,
    onRemoveFile: PropTypes.func.isRequired,
    isMobile: PropTypes.bool.isRequired,
};

const SupportingDocument = ({
    selectedFiles,
    handleRemoveFile,
    showDeleteButton = true,
    imageBaseUrl,
}) => {
    const { useWindowWidth } = useJunoCommonFunctions();
    const windowWidth = useWindowWidth();

    const [modalOpen, setModalOpen] = useState(false);
    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);
    const rowsPerPage = 5;

    useEffect(() => {
        if (selectedFiles) {
            const formattedFiles = selectedFiles.map((file, index) => ({
                key: index,
                Name: file?.name || file?.fileName,
                Size: formatFileSize(file?.size || file?.fileSize),
            }));
            setData(formattedFiles);
            setCurrentPage(1);
        }
    }, [selectedFiles]);

    const paginatedData = data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    const handleOpenModal = () => setModalOpen(true);
    const handleCloseModal = () => setModalOpen(false);

    const handleOpenDeleteModal = (fileKey) => {
        const fileData = data.find(item => item.key === fileKey);
        setFileToDelete(fileData);
        setDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setDeleteModalOpen(false);
        setFileToDelete(null);
    };

    const handleConfirmDelete = () => {
        if (fileToDelete) {
            handleRemoveFile(fileToDelete.key);
        }
        handleCloseDeleteModal();
    };

    const columns = [
        {
            label: 'Name',
            key: 'Name',
        },
        {
            label: 'Size',
            key: 'Size',
        },
    ];

    return (
        <div className="supportingDocuments w-full lg:w-[90%] m-auto lg:!mb-3 md:!my-6">
            <MediaQuery minWidth={992}>
                <div
                    className={`flex justify-between items-center  overflow-x-auto rounded-[8px] rounded-b-none border !border-section-br bg-modal-bg px-6 py-3`}>
                    <p className="!text-section-title  text-sm font-semibold leading-5 uppercase ">
                        Supporting Documents
                    </p>
                </div>
                <DataTable
                    columns={columns}
                    data={paginatedData}
                    customRenderCell={(row, data, index) => {
                        if (row.key == 'Name') {
                            return (
                                <div className="flex items-center gap-2">
                                    {showDeleteButton && (
                                        <button
                                            onClick={() => handleOpenDeleteModal(data.key)}
                                            className="bg-transparent border-none p-0 cursor-pointer">
                                            <DeleteIcon
                                                alt="Delete"
                                                width={20}
                                                height={20}
                                                className="text-text"
                                            />
                                        </button>
                                    )}
                                    <span>{data.Name}</span>
                                </div>
                            );
                        }
                    }}
                />
                <div className="flex justify-center pt-3 pb-2 bg-bulkpay-card-bg rounded-b-lg -mt-2">
                    <Pagination
                        data={data}
                        totalCount={data.length}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        pageSize={data.length ? rowsPerPage : 0}
                    />
                </div>
                
                {deleteModalOpen && fileToDelete && (
                    <ActionConfirmModal
                        open={deleteModalOpen}
                        heading="Delete Supporting Document!"
                        subHeading="Are you sure you want to delete the supporting document?"
                        onCancel={handleCloseDeleteModal}
                        onConfirm={handleConfirmDelete}
                        type="delete"
                        actionButtonText="delete"
                        body={() => (
                            <div>
                                <p className="text-[#18181B] dark:text-[#FFF] text-center text-base font-semibold leading-6 uppercase">
                                    {fileToDelete.Name}
                                </p>
                                <p className="text-[#51525C] dark:text-[#D1D1D6] text-center text-sm font-medium leading-5 uppercase">
                                    {fileToDelete.Size}
                                </p>
                            </div>
                        )}
                    />
                )}
            </MediaQuery>

            <MediaQuery maxWidth={991}>
                <MobileTabletDocumentsList
                    onRemoveFile={handleRemoveFile}
                    onViewAll={handleOpenModal}
                    files={data}
                    imageBaseUrl={imageBaseUrl}
                />
                <DocumentsModal
                    isMobile={windowWidth < 1110}
                    files={data}
                    onClose={handleCloseModal}
                    onRemoveFile={handleRemoveFile}
                    imageBaseUrl={imageBaseUrl}
                    open={modalOpen}
                />
            </MediaQuery>
        </div>
    );
};

export default SupportingDocument;
